/**
 * MobileAppControlPanel — the glass control panel that drops from the header
 * cutout while a user app is running (opened via the AppPanel signal, see
 * appPanel.tsx). It mirrors the controls the desktop HTML editor / AppWindow
 * expose, adapted to touch:
 *
 *   - standalone HTML (Mode A): Back · Forward · Refresh/Home · Share
 *     (the middle control is Refresh at the entry, Home once the app has
 *     navigated away — same Refresh↔Home toggle as HtmlEditor).
 *   - mini-app (Mode B): Refresh · Settings · Share
 *
 * Styled as a CONTEXT MENU, not a modal sheet: the frosted-glass `ws-menu`
 * kit (translucent + backdrop-blur — the same glass the header burger menu and
 * editor popovers use) with NO fog backdrop, dismissed by a transparent
 * full-screen click-catcher (the header burger's pattern). Portals to
 * document.body outside any ThemedArea, so it applies the theme class + the
 * load-bearing `ws-clear` — the latter keeps the wrapper transparent so the
 * glass blur samples the REAL app behind it (an opaque wrapper would make the
 * panel read as flat paint).
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ArrowRight, RotateCw, House, Settings, Share, type LucideIcon } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

interface Control {
  key: string
  icon: LucideIcon
  label: string
  onClick: () => void
  disabled?: boolean
}

export function MobileAppControlPanel({
  kind, hasNavigated, canBack, canForward, onBack, onForward, onReloadHome, onSettings, onShare, onClose,
}: {
  kind: 'miniapp' | 'html'
  /** HTML only — flips the middle control between Refresh and Home. */
  hasNavigated: boolean
  /** HTML only — there is a prior page to go back to. */
  canBack: boolean
  /** HTML only — the user went back and can move forward again. */
  canForward: boolean
  onBack: () => void
  onForward: () => void
  /** Refresh (at entry) / Home (return to entry) — same action, contextual label. */
  onReloadHome: () => void
  onSettings: () => void
  onShare: () => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Most controls close the panel after acting (menu-dismiss idiom). Back/
  // forward are the exception: they're repeatable and their enabled state
  // updates live, so keep the panel open to tap through history.
  const act = (fn: () => void) => () => { fn(); onClose() }

  const controls: Control[] = kind === 'html'
    ? [
        { key: 'back', icon: ArrowLeft, label: 'Back', onClick: onBack, disabled: !canBack },
        { key: 'forward', icon: ArrowRight, label: 'Forward', onClick: onForward, disabled: !canForward },
        // At the entry it's a Refresh; once navigated away it returns Home.
        hasNavigated
          ? { key: 'home', icon: House, label: 'Home', onClick: act(onReloadHome) }
          : { key: 'refresh', icon: RotateCw, label: 'Refresh', onClick: act(onReloadHome) },
        { key: 'share', icon: Share, label: 'Share', onClick: act(onShare) },
      ]
    : [
        { key: 'refresh', icon: RotateCw, label: 'Refresh', onClick: act(onReloadHome) },
        { key: 'settings', icon: Settings, label: 'Settings', onClick: act(onSettings) },
        { key: 'share', icon: Share, label: 'Share', onClick: act(onShare) },
      ]

  return createPortal(
    // ws-clear is LOAD-BEARING: without it the theme class paints an opaque
    // --bg-primary and the glass backdrop-filter has nothing to frost.
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200]`}>
      {/* Transparent click-catcher — NO fog. Tap anywhere off the menu to
          dismiss (the header burger menu's pattern). */}
      <div className="absolute inset-0" onClick={onClose} aria-hidden />
      {/* The menu drops from the header zone — cleared of the safe-area inset +
          the header row (≈68px). Centered via left/right+mx-auto (NOT a
          transform, which the drop animation owns). `ws-menu` supplies the
          frosted glass; width fits its content. */}
      <div
        className="ws-menu mobile-toggle-in absolute left-0 right-0 mx-auto flex w-max max-w-[92vw] items-stretch gap-0.5"
        style={{ top: 'calc(env(safe-area-inset-top) + 68px)' }}
        role="menu"
      >
        {controls.map(({ key, icon: Icon, label, onClick, disabled }) => (
          <button
            key={key}
            onClick={onClick}
            disabled={disabled}
            role="menuitem"
            aria-label={label}
            className="flex min-w-[64px] flex-col items-center gap-1.5 rounded-lg px-2 py-2.5 themed-text hover:themed-bg-tertiary active:themed-bg-tertiary disabled:pointer-events-none disabled:opacity-40"
          >
            <Icon size={20} className="shrink-0" />
            <span className="text-[11px] themed-text-secondary">{label}</span>
          </button>
        ))}
      </div>
    </div>,
    document.body,
  )
}
